import { useStore } from 'vuex'
import { ref, computed, getCurrentInstance } from 'vue'
import {
    isDocument,
    getDocumentIcon
} from 'foundation/js/utils/communityAttachmentUtils'

export default {
    name: 'AttachmentsEditor',
    props: {
        acceptedFileTypes: {
            type: String,
            required: false,
            default:
                '.doc, .docx, .xls, .xlsx, .pdf, .gif, .jpeg, .jpg, .tiff, .bmp, .png'
        },
        files: {
            type: Array,
            required: true,
            default: () => []
        },
        fileErrorMessage: {
            type: String,
            required: false,
            default: ''
        },
        maxAttachments: {
            type: Number,
            required: false
        },
        maxFileSizeBytes: {
            type: Number,
            required: false,
            default: 10 * 1024 * 1024 // 10 MB
        }
    },

    setup(props, { emit }) {
        const store = useStore()

        const isValidationError = ref(false)
        const isFileSizeError = ref(false)

        const instance = getCurrentInstance()
        const uploadInputId = computed(() => `uploadFile-${instance.uid}`)



        const initialMaxId = Math.max(
            0,
            ...props.files.map((f) => (Number.isFinite(+f.id) ? +f.id : -1))
        )
        const fileIdCount = ref(initialMaxId + 1)

        const files = ref(
            props.files.map((file) => ({ ...file, isOriginal: true }))
        )

        const filesToRemove = computed(() =>
            props.files
                .filter((file) => !files.value.some((f) => f.id === file.id))
                .map((file) => file.id)
        )

        const filesToAdd = computed(() =>
            files.value
                .filter((file) => !file.isOriginal)
                .map((file) => file.file)
        )

        const maxAttachmentsReached = computed(() => props.maxAttachments && props.maxAttachments <= files.value.length)

        const clearValidationError = () => {
            isValidationError.value = false
            isFileSizeError.value = false
        }

        const addFile = (e) => {
            clearValidationError()

            const eventFiles = e.target.files
            if (!eventFiles || !eventFiles.length) return

            if (maxAttachmentsReached.value) {
                return
            }

            Array.from(eventFiles).forEach((eventFile) => {
                if (eventFile.size > props.maxFileSizeBytes) {
                    var fileSizeLabelSet = getLabel("fileSizeErrorMessage")
                    if (!fileSizeLabelSet)
                    {
                        isValidationError.value = true
                    }
                    else
                    {
                        isFileSizeError.value = true
                    }
                    return
                }

                const newFile = {
                    filename: eventFile.name,
                    size: eventFile.size,
                    id: fileIdCount.value++,
                    url: URL.createObjectURL(eventFile),
                    isOriginal: false,
                    file: eventFile
                }

                files.value.push(newFile)
            })

            e.target.value = ''

            emit('update:filesToAdd', filesToAdd.value)
            setTimeout(() => {
                if (document.activeElement === e.target) {
                    e.target.blur()
                }
            }, 25)
        }

        const removeFile = (fileToRemove) => {
            clearValidationError()

            if (!fileToRemove.isOriginal && fileToRemove.url) {
                try {
                    URL.revokeObjectURL(fileToRemove.url)
                } catch (_) {}
            }

            files.value = files.value.filter(
                (file) => file.id !== fileToRemove.id
            )

            emit('update:filesToAdd', filesToAdd.value)
            emit('update:filesToRemove', filesToRemove.value)
        }

        const clear = () => {
            files.value.forEach((f) => {
                if (!f.isOriginal && f.url) {
                    try {
                        URL.revokeObjectURL(f.url)
                    } catch (_) {}
                }
            })
            files.value = []
            emit('update:filesToAdd', filesToAdd.value)
            emit('update:filesToRemove', filesToRemove.value)
        }

        const formatFileSize = (size) => {
            if (typeof size === 'string') return size

            const sizeKB = size / 1024
            if (sizeKB < 1000) return `${sizeKB.toFixed(1)} KB`

            const sizeMB = sizeKB / 1000
            return `${sizeMB.toFixed(3)} MB`
        }

        const getLabel = (labelKey, allowEmpty = false) =>
            store.getters['labels/getLabel']({
                labelCollection: 'AttachmentsEditor',
                labelKey,
                allowEmpty
            })

        return {
            getLabel,
            formatFileSize,
            isValidationError,
            isFileSizeError,
            addFile,
            removeFile,
            clear,
            getDocumentIcon,
            isDocument,
            files,
            maxAttachmentsReached,
            uploadInputId
        }
    }
}
